import { useEffect, useState } from "react";

import useUser from "@/users/useUser";
import getIdentifier from "./getIdentifier";
import getPayload from "./getPayload";
import initializeListeners from "./initializeListeners";
import useImportIfVisible from "./useImportIsVisible";

const TSOPHIEL_URL = process.env["NEXT_PUBLIC_TSOPHIEL_URL"];

const SOCKET_OPEN_STATE = 1;
const SOCKET_CLOSE_NORMAL = 1000;
const RECONNECT_TIMEOUT_LENGTH = 1500;
const ALLOWED_RECONNECT_ATTEMPTS = 5;

interface UseReadingTrackerProps {
  articleLevelId?: number | null | undefined;
  contentId: string | null | undefined;
}

const useReadingTracker = ({
  articleLevelId,
  contentId,
}: UseReadingTrackerProps) => {
  const { data: user } = useUser();

  const [ifVisible, setIfVisible] = useState();

  // Import ifvisible.js, which is used to track the idle time on the page.
  // We import ifvisible dynamically to avoid an SSR error caused by the library using document.
  useImportIfVisible(setIfVisible);

  // Handle the websocket connection and initialize the event listeners.
  useEffect(() => {
    if (!articleLevelId && !contentId) return;

    if (!TSOPHIEL_URL || TSOPHIEL_URL === "undefined") {
      console.warn("TSOPHIEL_URL is undefined.");
      return;
    }

    let socket = new WebSocket(TSOPHIEL_URL);
    let timeStarted = new Date().getTime();
    let reconnectAttempts = 0;
    const pendingTimeouts: NodeJS.Timeout[] = [];

    // Handle initial WebSocket errors (in case it is blocked by district)
    try {
      socket = new WebSocket(TSOPHIEL_URL);
    } catch (error) {
      const errorMessage = "Reading Websocket error: " + JSON.stringify(error);
      console.error(errorMessage);
      return;
    }

    const sendDelayedMessage = (additionalProperties: any) => {
      const pendingTimeout = setTimeout(() => {
        const messageProperties = {
          identifier: getIdentifier(),
          levelId: articleLevelId,
          contentId,
          timeStarted,
          userId: user?.id,
          userUniqueId: user?.unique_id,
        };

        const payload = getPayload(messageProperties, additionalProperties);

        if (socket.readyState === SOCKET_OPEN_STATE) {
          socket.send(payload);
        } else if (reconnectAttempts < ALLOWED_RECONNECT_ATTEMPTS) {
          sendDelayedMessage(additionalProperties);
        }
      }, 250);

      pendingTimeouts.push(pendingTimeout);
    };

    const onSocketOpen = () => {
      reconnectAttempts = 0;
      sendDelayedMessage({});
    };

    const onSocketError = (error: any) => {
      const errorMessage = "Reading Websocket error: " + JSON.stringify(error);
      console.error(errorMessage);
    };

    const onSocketClose = (event: any) => {
      if (event.code === SOCKET_CLOSE_NORMAL) {
        return;
      }

      // Attempt to reconnect.
      if (
        socket.readyState !== SOCKET_OPEN_STATE &&
        reconnectAttempts < ALLOWED_RECONNECT_ATTEMPTS
      ) {
        const pendingTimeout = setTimeout(() => {
          timeStarted = new Date().getTime();
          reconnectAttempts = reconnectAttempts + 1;

          socket = new WebSocket(TSOPHIEL_URL);
          socket.onopen = onSocketOpen;
          socket.onclose = onSocketClose;
          socket.onerror = onSocketError;
        }, RECONNECT_TIMEOUT_LENGTH * reconnectAttempts);

        pendingTimeouts.push(pendingTimeout);
      }
    };

    socket.onopen = onSocketOpen;
    socket.onclose = onSocketClose;
    socket.onerror = onSocketError;

    const cleanUpListeners = initializeListeners(ifVisible, sendDelayedMessage);

    return () => {
      cleanUpListeners();
      pendingTimeouts.forEach((timeout) => clearTimeout(timeout));
      socket.close(SOCKET_CLOSE_NORMAL);
    };
  }, [articleLevelId, ifVisible, user?.id]);
};

export default useReadingTracker;
